import { Chess } from 'chess.js';
import { getAccessToken, getUser, isSupabaseAuthConfigured, refreshSession } from './supabaseAuth';
import { fetchLichessPuzzleReplay } from './lichessGateway';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY =
  process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY ||
  process.env.REACT_APP_SUPABASE_ANON_KEY;

function hasConfig() {
  return Boolean(SUPABASE_URL && SUPABASE_KEY);
}

const PUZZLE_THEME_META = {
  matein1: {
    ro: { title: 'Mat în 1', summary: 'Finalizează instant: găsește mutarea decisivă dintr-o singură lovitură.' },
    en: { title: 'Mate in 1', summary: 'Finish instantly: find the decisive move in a single strike.' }
  },
  middlegame: {
    ro: { title: 'Middlegame', summary: 'Planuri de joc, coordonare de piese și inițiativă în jocul de mijloc.' },
    en: { title: 'Middlegame', summary: 'Planning, piece coordination, and initiative in the middle game.' }
  },
  matein2: {
    ro: { title: 'Mat în 2', summary: 'Calculează secvențe forțate în două mutări și închide partida precis.' },
    en: { title: 'Mate in 2', summary: 'Calculate forcing two-move sequences and finish with precision.' }
  },
  fork: {
    ro: { title: 'Furculiță', summary: 'Atac dublu pe ținte critice pentru câștig de material sau inițiativă.' },
    en: { title: 'Fork', summary: 'Double attacks on critical targets for material gain or initiative.' }
  },
  onemove: {
    ro: { title: 'Best One Move', summary: 'Găsește instant mutarea cea mai puternică din poziție.' },
    en: { title: 'Best One Move', summary: 'Find the strongest move immediately from the position.' }
  },
  discoveredattack: {
    ro: { title: 'Atac Descoperit', summary: 'Deschide liniile ascunse și creează amenințări decisive.' },
    en: { title: 'Discovered Attack', summary: 'Uncover hidden lines and create decisive threats.' }
  },
  defensivemove: {
    ro: { title: 'Mutare Defensivă', summary: 'Apără corect poziția și neutralizează amenințarea adversă.' },
    en: { title: 'Defensive Move', summary: 'Defend accurately and neutralize the opponent threat.' }
  },
  opening: {
    ro: { title: 'Opening', summary: 'Decizii corecte în debut: dezvoltare, centru și siguranța regelui.' },
    en: { title: 'Opening', summary: 'Correct opening decisions: development, center, and king safety.' }
  },
  promotion: {
    ro: { title: 'Promovare', summary: 'Activează pionul trecut și transformă avantajul în damă.' },
    en: { title: 'Promotion', summary: 'Activate passed pawns and convert your edge into promotion.' }
  }
};

export const PUZZLE_THEME_IDS = [
  'matein1',
  'middlegame',
  'matein2',
  'fork',
  'onemove',
  'discoveredattack',
  'defensivemove',
  'opening',
  'promotion'
];

export const PUZZLE_ACTIVE_THEME_IDS = [...PUZZLE_THEME_IDS];

export const PUZZLE_COMING_SOON_THEME_IDS = [
  'mate',
  'endgame',
  'kingsideattack',
  'hangingpiece',
  'queenendgame',
  'rookendgame',
  'discoveredcheck',
  'attackingf2f7',
  'pillsburysmate',
  'pin',
  'doublecheck',
  'operamate',
  'smotheredmate',
  'queensideattack',
  'advancedpawn',
  'cornermate',
  'queenrookendgame',
  'hookmate',
  'morphysmate',
  'dovetailmate',
  'blindswinemate',
  'trianglemate',
  'arabianmate',
  'doublebishopmate',
  'bodenmate',
  'killboxmate',
  'anastasiamate',
  'bishopendgame',
  'knightendgame',
  'vukovicmate',
  'balestramate',
  'equality',
  'pawnendgame',
  'enpassant'
];

const solvedPuzzleCache = new Map();
const lichessPuzzleCache = new Map();

function parseUci(uci) {
  const s = String(uci || '').trim().toLowerCase();
  if (s.length < 4) return null;
  const from = s.slice(0, 2);
  const to = s.slice(2, 4);
  const promotion = s.length >= 5 ? s[4] : undefined;
  return { from, to, promotion };
}

function toJsonOrThrow(text, fallbackMessage) {
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    throw new Error(fallbackMessage || 'Invalid JSON response');
  }
}

async function apiGet(path, token) {
  const response = await fetch(`${SUPABASE_URL}${path}`, {
    cache: 'no-store',
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${token || SUPABASE_KEY}`
    }
  });

  const text = await response.text();
  if (!response.ok) {
    const err = new Error(text || 'Supabase request failed');
    err.status = response.status;
    throw err;
  }
  return toJsonOrThrow(text || '[]', 'Failed to parse GET response');
}

async function apiRpc(fnName, payload) {
  const doRequest = async (token) => {
    const response = await fetch(`${SUPABASE_URL}/rest/v1/rpc/${fnName}`, {
      method: 'POST',
      headers: {
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload || {})
    });
    const text = await response.text();
    if (!response.ok) {
      const err = new Error(text || `Supabase rpc ${fnName} failed`);
      err.status = response.status;
      throw err;
    }
    return toJsonOrThrow(text || 'null', `Failed to parse rpc ${fnName} response`);
  };

  const token = getAccessToken();
  if (!token) throw new Error('Not authenticated');

  try {
    return await doRequest(token);
  } catch (e) {
    if (e && Number(e.status) === 401) {
      const refreshed = await refreshSession();
      if (!refreshed?.access_token) throw e;
      return doRequest(refreshed.access_token);
    }
    throw e;
  }
}

export async function fetchSolvedPuzzleIdSet({ userId = '', maxAgeMs = 60_000 } = {}) {
  if (!hasConfig() || !userId) return new Set();

  const key = String(userId);
  const now = Date.now();
  const cached = solvedPuzzleCache.get(key);
  if (cached && now - Number(cached.ts || 0) <= maxAgeMs) {
    return cached.ids;
  }

  try {
    const rows = await apiGet(
      `/rest/v1/puzzle_attempts?select=puzzle_catalog_id&user_id=eq.${encodeURIComponent(
        userId
      )}&or=(success.eq.true,result.eq.success,result.eq.success_hint)&limit=50000`,
      getAccessToken() || SUPABASE_KEY
    );
    const ids = new Set((rows || []).map((r) => Number(r?.puzzle_catalog_id)).filter((n) => Number.isFinite(n)));
    solvedPuzzleCache.set(key, { ts: now, ids });
    return ids;
  } catch {
    return new Set();
  }
}

function explanationFromThemes(themes, language) {
  const t = new Set((themes || []).map((x) => String(x).toLowerCase()));
  if (t.has('matein2')) {
    return language === 'ro'
      ? 'Mutarea corectă pornește o secvență forțată în două mutări: adversarul rămâne fără resurse reale.'
      : 'The correct move starts a forced mate-in-two sequence: the opponent has no real resources.';
  }
  if (t.has('matein1') || t.has('mate')) {
    return language === 'ro'
      ? 'Aceasta este cea mai bună mutare pentru că forțează imediat finalul: adversarul nu mai are apărare eficientă.'
      : 'This is the best move because it forces an immediate finish: the opponent has no effective defense.';
  }
  if (t.has('fork')) {
    return language === 'ro'
      ? 'Mutarea este cea mai bună deoarece creează atac dublu și câștig de material.'
      : 'This is best because it creates a fork and wins material.';
  }
  if (t.has('pin')) {
    return language === 'ro'
      ? 'Mutarea fixează piesa adversă și limitează apărarea, oferind avantaj concret.'
      : 'This move pins an enemy piece, limits defense, and yields concrete advantage.';
  }
  if (t.has('skewer')) {
    return language === 'ro'
      ? 'Mutarea exploatează alinierea pieselor: piesa valoroasă este forțată să plece, iar următoarea cade.'
      : 'This move exploits alignment: the high-value piece is forced away and the next piece drops.';
  }
  if (t.has('pawnendgame') || t.has('endgame')) {
    return language === 'ro'
      ? 'În final, mutarea cea mai bună câștigă tempo și îmbunătățește poziția regelui/pionilor decisiv.'
      : 'In endgames, the best move gains tempo and decisively improves king/pawn activity.';
  }
  if (t.has('defensivemove')) {
    return language === 'ro'
      ? 'Mutarea corectă reduce pericolul imediat și păstrează poziția sub control.'
      : 'The right move neutralizes immediate danger and keeps the position under control.';
  }
  if (t.has('opening')) {
    return language === 'ro'
      ? 'În debut, mutarea optimă accelerează dezvoltarea și menține controlul centrului.'
      : 'In the opening, the best move speeds up development and keeps center control.';
  }
  if (t.has('promotion')) {
    return language === 'ro'
      ? 'Mutarea corectă susține promovarea pionului și transformă avantajul în material decisiv.'
      : 'The best move supports pawn promotion and converts the edge into decisive material.';
  }
  if (t.has('middlegame')) {
    return language === 'ro'
      ? 'Mutarea cea mai bună îmbunătățește coordonarea pieselor și păstrează inițiativa în jocul de mijloc.'
      : 'The best move improves piece coordination and keeps the initiative in the middlegame.';
  }
  return language === 'ro'
    ? 'Aceasta este cea mai bună mutare deoarece păstrează inițiativa și maximizează avantajul pozițional.'
    : 'This is the best move because it keeps the initiative and maximizes positional advantage.';
}

function missionByThemes(themes, language, turn) {
  const t = new Set((themes || []).map((x) => String(x).toLowerCase()));
  const sideToMoveLabel =
    language === 'ro'
      ? turn === 'w'
        ? 'Alb'
        : 'Negru'
      : turn === 'w'
        ? 'White'
        : 'Black';

  if (t.has('matein1')) {
    return language === 'ro'
      ? `Mat în 1 · ${sideToMoveLabel} la mutare. Lovește decisiv: găsește mutarea care închide partida acum.`
      : `Mate in 1 · ${sideToMoveLabel} to move. Strike decisively: find the move that ends the game now.`;
  }
  if (t.has('matein2')) {
    return language === 'ro'
      ? `Mat în 2 · ${sideToMoveLabel} la mutare. Calculează două mutări înainte și forțează finalul.`
      : `Mate in 2 · ${sideToMoveLabel} to move. Calculate two moves ahead and force the finish.`;
  }
  if (t.has('matein3')) {
    return language === 'ro'
      ? `Mat în 3 · ${sideToMoveLabel} la mutare. Construiește secvența forțată până la mat.`
      : `Mate in 3 · ${sideToMoveLabel} to move. Build the forcing sequence all the way to mate.`;
  }
  if (t.has('onemove')) {
    return language === 'ro'
      ? `Best One Move · ${sideToMoveLabel} la mutare. Găsește instant mutarea cu impact maxim.`
      : `Best One Move · ${sideToMoveLabel} to move. Find the move with immediate maximum impact.`;
  }
  if (t.has('fork')) {
    return language === 'ro'
      ? `Furculiță tactică · ${sideToMoveLabel} la mutare. Găsește atacul dublu care câștigă material.`
      : `Fork tactic · ${sideToMoveLabel} to move. Find the double attack that wins material.`;
  }
  if (t.has('pin')) {
    return language === 'ro'
      ? `Țintuire · ${sideToMoveLabel} la mutare. Fixează piesa cheie și transformă presiunea în avantaj.`
      : `Pin tactic · ${sideToMoveLabel} to move. Immobilize the key piece and convert pressure into advantage.`;
  }
  if (t.has('skewer')) {
    return language === 'ro'
      ? `Skewer · ${sideToMoveLabel} la mutare. Atacă prin aliniere și câștigă piesa din spate.`
      : `Skewer tactic · ${sideToMoveLabel} to move. Attack through alignment and win the piece behind.`;
  }
  if (t.has('discoveredattack') || t.has('discoveredcheck')) {
    return language === 'ro'
      ? `Atac descoperit · ${sideToMoveLabel} la mutare. Eliberează linia și declanșează lovitura ascunsă.`
      : `Discovered attack · ${sideToMoveLabel} to move. Uncover the line and unleash the hidden strike.`;
  }
  if (t.has('defensivemove')) {
    return language === 'ro'
      ? `Mutare defensivă · ${sideToMoveLabel} la mutare. Oprește planul advers și stabilizează poziția.`
      : `Defensive move · ${sideToMoveLabel} to move. Stop the opponent plan and stabilize the position.`;
  }
  if (t.has('opening')) {
    return language === 'ro'
      ? `Opening · ${sideToMoveLabel} la mutare. Alege mutarea de dezvoltare care susține planul corect.`
      : `Opening · ${sideToMoveLabel} to move. Choose the developing move that supports the right plan.`;
  }
  if (t.has('promotion')) {
    return language === 'ro'
      ? `Promovare · ${sideToMoveLabel} la mutare. Creează calea sigură pentru pionul decisiv.`
      : `Promotion · ${sideToMoveLabel} to move. Create the safe path for the decisive pawn.`;
  }
  if (t.has('middlegame')) {
    return language === 'ro'
      ? `Middlegame · ${sideToMoveLabel} la mutare. Găsește mutarea care întărește poziția și planul tău.`
      : `Middlegame · ${sideToMoveLabel} to move. Find the move that strengthens your position and plan.`;
  }
  if (t.has('pawnendgame') || t.has('rookendgame') || t.has('endgame')) {
    return language === 'ro'
      ? `Final de partidă · ${sideToMoveLabel} la mutare. Alege mutarea precisă care transformă avantajul în punct.`
      : `Endgame precision · ${sideToMoveLabel} to move. Choose the precise move that converts your edge.`;
  }

  return language === 'ro'
    ? `Găsește cea mai bună mutare · ${sideToMoveLabel} la mutare. Joacă precis și păstrează inițiativa.`
    : `Find the best move · ${sideToMoveLabel} to move. Play precisely and keep the initiative.`;
}

function buildPuzzleLessonFromRecord(record, language) {
  const first = parseUci(record.first_uci || (record.moves_uci || [])[0]);
  const best = parseUci(record.best_uci || (record.moves_uci || [])[1]);
  const fullLine = Array.isArray(record.moves_uci) ? record.moves_uci.map(parseUci).filter(Boolean) : [];
  const solutionLine = fullLine.slice(1); // after first move is applied to reach player-facing puzzle position

  let fenForPlayer = record.fen_before;
  try {
    if (first?.from && first?.to) {
      const chess = new Chess(record.fen_before);
      chess.move({ from: first.from, to: first.to, promotion: first.promotion });
      fenForPlayer = chess.fen();
    }
  } catch {
    fenForPlayer = record.fen_before;
  }

  const expected = best?.from && best?.to ? { from: best.from, to: best.to } : null;
  const puzzleLabel = `#${record.source_puzzle_id || record.id}`;
  const explanation = explanationFromThemes(record.themes || [], language);
  const fenParts = String(fenForPlayer || '').trim().split(/\s+/);
  const turn = String(fenParts[1] || 'w').toLowerCase() === 'b' ? 'b' : 'w';
  const sideToMoveLabel =
    language === 'ro'
      ? turn === 'w'
        ? 'Alb'
        : 'Negru'
      : turn === 'w'
        ? 'White'
        : 'Black';

  return {
    id: `db-puzzle-${record.id}`,
    puzzleCatalogId: record.id,
    sourcePuzzleId: record.source_puzzle_id,
    title: language === 'ro' ? `Puzzle ${puzzleLabel}` : `Puzzle ${puzzleLabel}`,
    description:
      language === 'ro'
        ? 'Găsește cea mai bună mutare. Soluția și explicația apar doar după rezolvare.'
        : 'Find the best move. Solution and explanation appear only after solving.',
    durationMin: 2,
    lessonType: 'puzzle',
    puzzleMeta: {
      rating: record.rating,
      popularity: record.popularity,
      nbPlays: record.nb_plays,
      sideToMove: sideToMoveLabel,
      themes: record.themes || [],
      gameUrl: record.game_url || ''
    },
    segments: [
      {
        id: 1,
        fen: fenForPlayer,
        text:
          language === 'ro'
            ? `Ești ${sideToMoveLabel} la mutare. Găsește cea mai bună mutare din poziție.`
            : `You are ${sideToMoveLabel} to move. Find the best move in the position.`,
        interaction: {
          type: 'move',
          lockNextUntilCorrect: true,
          expected,
          solutionMoves: solutionLine,
          stepTitle: { ro: 'Puzzle', en: 'Puzzle' },
          prompt: {
            ro: missionByThemes(record.themes || [], 'ro', turn),
            en: missionByThemes(record.themes || [], 'en', turn)
          },
          explanation: {
            ro: `${explanation} Soluția: ${best?.from || '?'} → ${best?.to || '?'}.`,
            en: `${explanation} Solution: ${best?.from || '?'} → ${best?.to || '?'}.`
          }
        },
        moves: []
      }
    ]
  };
}

function normalizeLichessRecord(row) {
  if (!row) return null;
  const sourceId = String(row.source_puzzle_id || '').trim();
  if (!sourceId) return null;
  const list = Array.isArray(row.moves_uci)
    ? row.moves_uci
    : String(row.moves_uci || '').split(/\s+/).filter(Boolean);
  if (!String(row.fen_before || '').trim() || list.length === 0) return null;
  return {
    id: `lichess-${sourceId}`,
    source: 'lichess-api',
    source_puzzle_id: sourceId,
    fen_before: row.fen_before,
    moves_uci: list,
    first_uci: row.first_uci || list[0] || '',
    best_uci: row.best_uci || list[1] || list[0] || '',
    rating: Number(row.rating || 0) || 0,
    popularity: Number(row.popularity || 0) || 0,
    nb_plays: Number(row.nb_plays || 0) || 0,
    themes: Array.isArray(row.themes) ? row.themes : [],
    game_url: row.game_url || ''
  };
}

export async function fetchMateInOnePuzzles({
  limit = 10,
  userId = '',
  minRating = null,
  maxRating = null,
  solvedSet = null
} = {}) {
  if (!hasConfig()) return [];

  // Keep DB query cheap (theme filter + small pool, no expensive ORDER BY).
  // We sort client-side after filtering solved puzzles.
  const poolLimit = Math.max(40, limit * 6);
  const rows = await apiGet(
    `/rest/v1/puzzles_catalog?select=id,source_puzzle_id,fen_before,moves_uci,first_uci,best_uci,rating,popularity,nb_plays,themes,game_url&is_best_one_move=eq.true&themes=cs.%7Bmatein1%7D&limit=${poolLimit}`
  );

  const solved =
    solvedSet instanceof Set
      ? solvedSet
      : (userId ? await fetchSolvedPuzzleIdSet({ userId }) : new Set());

  const parseNullableNumber = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const minR = parseNullableNumber(minRating);
  const maxR = parseNullableNumber(maxRating);

  const unsolved = (rows || []).filter((r) => {
    if (solved.has(Number(r.id))) return false;
    const rr = Number(r?.rating || 0);
    if (minR != null && rr < minR) return false;
    if (maxR != null && rr > maxR) return false;
    return true;
  });
  unsolved.sort((a, b) => {
    const playsA = Number(a?.nb_plays || 0);
    const playsB = Number(b?.nb_plays || 0);
    if (playsB !== playsA) return playsB - playsA;
    const ratingA = Number(a?.rating || 0);
    const ratingB = Number(b?.rating || 0);
    if (ratingA !== ratingB) return ratingA - ratingB;
    return Number(a?.id || 0) - Number(b?.id || 0);
  });
  return unsolved.slice(0, limit);
}

export async function fetchPuzzlesByTheme({
  theme = 'matein1',
  limit = 10,
  userId = '',
  minRating = null,
  maxRating = null,
  solvedSet = null
} = {}) {
  const themeId = String(theme || '').trim().toLowerCase();
  if (!themeId) return [];
  if (themeId === 'matein1' && hasConfig()) {
    return fetchMateInOnePuzzles({ limit, userId, minRating, maxRating, solvedSet });
  }

  const parseNullableNumber = (v) => {
    if (v === null || v === undefined || v === '') return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };
  const minR = parseNullableNumber(minRating);
  const maxR = parseNullableNumber(maxRating);

  const loadLichessFallback = async () => {
    try {
      const remoteRows = await fetchLichessPuzzleReplay({
        days: 30,
        theme: themeId,
        limit: Math.max(1, Number(limit || 10))
      });
      const normalized = remoteRows.map(normalizeLichessRecord).filter(Boolean);
      normalized.forEach((row) => {
        const key = String(row.source_puzzle_id || '').trim();
        if (key) lichessPuzzleCache.set(key, row);
      });
      return normalized;
    } catch {
      return [];
    }
  };

  if (!hasConfig()) {
    return loadLichessFallback();
  }

  const loadThemePoolRest = async (requireBestOneMove = true) => {
    const poolLimit = Math.max(24, limit * 3);
    const bestFilter = requireBestOneMove ? '&is_best_one_move=eq.true' : '';
    return apiGet(
      `/rest/v1/puzzles_catalog?select=id,source_puzzle_id,fen_before,moves_uci,first_uci,best_uci,rating,popularity,nb_plays,themes,game_url${bestFilter}&themes=cs.%7B${encodeURIComponent(themeId)}%7D&limit=${poolLimit}`
    );
  };

  // Fast path: authenticated RPC performs filtering + solved exclusion in DB.
  if (userId) {
    try {
      const rpcRows = await apiRpc('get_theme_puzzles_for_user', {
        p_theme: themeId,
        // DB already excludes solved + applies rating window, so no oversized pool needed.
        p_limit: Math.max(1, Number(limit || 10)),
        p_min_rating: minR,
        p_max_rating: maxR
      });
      const rows = Array.isArray(rpcRows) ? rpcRows : [];
      if (rows.length > 0) return rows.slice(0, limit);
      // If RPC returns no rows, continue with REST fallback path.
    } catch {
      // fallback to REST path below
    }
  }

  let rows = await loadThemePoolRest(true);
  if (!rows || rows.length === 0) {
    // Relax filter for themes where strict one-move pool can be empty (e.g. matein2/fork).
    rows = await loadThemePoolRest(false);
  }

  const solved =
    solvedSet instanceof Set
      ? solvedSet
      : (userId ? await fetchSolvedPuzzleIdSet({ userId }) : new Set());

  const unsolved = (rows || []).filter((r) => {
    if (solved.has(Number(r.id))) return false;
    const rr = Number(r?.rating || 0);
    if (minR != null && rr < minR) return false;
    if (maxR != null && rr > maxR) return false;
    return true;
  });
  unsolved.sort((a, b) => {
    const playsA = Number(a?.nb_plays || 0);
    const playsB = Number(b?.nb_plays || 0);
    if (playsB !== playsA) return playsB - playsA;
    const ratingA = Number(a?.rating || 0);
    const ratingB = Number(b?.rating || 0);
    if (ratingA !== ratingB) return ratingA - ratingB;
    return Number(a?.id || 0) - Number(b?.id || 0);
  });
  const finalRows = unsolved.slice(0, limit);
  if (finalRows.length > 0) return finalRows;
  return loadLichessFallback();
}

export function mapMateInOneToUiCategory(rows, language) {
  const list = (rows || []).map((r) => ({
    id: `db-puzzle-${r.id}`,
    puzzleCatalogId: r.id,
    title:
      language === 'ro'
        ? `Mate în 1 · #${r.source_puzzle_id}`
        : `Mate in 1 · #${r.source_puzzle_id}`,
    description:
      language === 'ro'
        ? 'Găsește cea mai bună mutare. Soluția se arată la final.'
        : 'Find the best move. Solution is shown at the end.',
    duration: language === 'ro' ? '~2 min' : '~2 min',
    difficulty:
      language === 'ro'
        ? `Rating ${r.rating}`
        : `Rating ${r.rating}`,
    rating: r.rating,
    popularity: r.popularity,
    nbPlays: r.nb_plays,
    themes: r.themes || []
  }));

  return {
    id: 'matein1',
    title: language === 'ro' ? 'Mate în 1' : 'Mate in 1',
    summary:
      language === 'ro'
        ? 'Antrenament personalizat: exerciții tactice selectate pentru progres constant și decizii de nivel înalt.'
        : 'Personalized training: tactical exercises selected for steady progress and high-level decisions.',
    puzzles: list
  };
}

export function mapThemeRowsToUiCategory({ theme, rows, language }) {
  const themeId = String(theme || '').trim().toLowerCase();
  const prettifyThemeId = (id) =>
    String(id || '')
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([a-z])(\d)/g, '$1 $2')
      .replace(/(\d)([a-z])/g, '$1 $2')
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (c) => c.toUpperCase());
  const meta = PUZZLE_THEME_META[themeId] || {
    ro: { title: prettifyThemeId(themeId) || 'Puzzle', summary: 'Categorie tactică disponibilă în curând.' },
    en: { title: prettifyThemeId(themeId) || 'Puzzle', summary: 'Tactical category coming soon.' }
  };

  const list = (rows || []).map((r) => ({
    id: String(r?.source || '').toLowerCase() === 'lichess-api'
      ? `lichess-puzzle-${r.source_puzzle_id}`
      : `db-puzzle-${r.id}`,
    puzzleCatalogId: typeof r?.id === 'number' ? r.id : null,
    title:
      language === 'ro'
        ? `${meta.ro.title} · #${r.source_puzzle_id}`
        : `${meta.en.title} · #${r.source_puzzle_id}`,
    description:
      language === 'ro'
        ? 'Găsește cea mai bună mutare. Soluția se arată la final.'
        : 'Find the best move. Solution is shown at the end.',
    duration: language === 'ro' ? '~2 min' : '~2 min',
    difficulty:
      language === 'ro'
        ? `Rating ${r.rating}`
        : `Rating ${r.rating}`,
    rating: r.rating,
    popularity: r.popularity,
    nbPlays: r.nb_plays,
    themes: r.themes || []
  }));

  return {
    id: themeId,
    title: language === 'ro' ? meta.ro.title : meta.en.title,
    summary: language === 'ro' ? meta.ro.summary : meta.en.summary,
    puzzles: list,
    available: list.length > 0
  };
}

export async function fetchPuzzleLessonById(puzzleCatalogId, language) {
  if (!hasConfig()) return null;
  const rows = await apiGet(
    `/rest/v1/puzzles_catalog?select=id,source_puzzle_id,fen_before,moves_uci,first_uci,best_uci,rating,popularity,nb_plays,themes,game_url&is_best_one_move=eq.true&id=eq.${Number(puzzleCatalogId)}&limit=1`
  );
  const row = (rows || [])[0];
  if (!row) return null;
  return buildPuzzleLessonFromRecord(row, language);
}

export async function fetchLichessPuzzleLessonById(sourcePuzzleId, language) {
  const key = String(sourcePuzzleId || '').trim();
  if (!key) return null;

  let row = normalizeLichessRecord(lichessPuzzleCache.get(key));
  if (!row) {
    try {
      const fetched = await fetchLichessPuzzleReplay({
        days: 30,
        theme: 'mix',
        limit: 20
      });
      fetched.forEach((entry) => {
        const normalized = normalizeLichessRecord(entry);
        if (!normalized) return;
        const cacheKey = String(normalized.source_puzzle_id || '').trim();
        if (!cacheKey) return;
        lichessPuzzleCache.set(cacheKey, normalized);
      });
      row = normalizeLichessRecord(lichessPuzzleCache.get(key));
    } catch {
      row = null;
    }
  }
  if (!row) return null;
  const lesson = buildPuzzleLessonFromRecord(row, language);
  return {
    ...lesson,
    id: `lichess-puzzle-${key}`,
    puzzleCatalogId: null,
    sourcePuzzleId: key
  };
}

export async function markPuzzleSolved({
  puzzleCatalogId,
  result = '',
  playedMoveUci = '',
  tries = 1,
  usedSolution = false,
  solveMs = null
}) {
  if (!isSupabaseAuthConfigured()) return null;
  const user = getUser();
  if (!user?.id) return null;
  if (!puzzleCatalogId) return null;

  const normalizedResult = String(result || '').trim().toLowerCase();
  const useExtendedResult = Boolean(normalizedResult);
  const payload = useExtendedResult
    ? {
        p_puzzle_catalog_id: Number(puzzleCatalogId),
        p_result: normalizedResult,
        p_played_move_uci: playedMoveUci || null,
        p_tries: Number(tries || 1),
        p_used_solution: Boolean(usedSolution),
        p_solve_ms: Number.isFinite(Number(solveMs)) ? Number(solveMs) : null
      }
    : {
        p_puzzle_catalog_id: Number(puzzleCatalogId),
        p_played_move_uci: playedMoveUci || null,
        p_tries: Number(tries || 1),
        p_used_solution: Boolean(usedSolution),
        p_solve_ms: Number.isFinite(Number(solveMs)) ? Number(solveMs) : null
      };

  const res = await apiRpc('solve_puzzle_and_award', payload);

  if (Array.isArray(res)) return res[0] || null;
  return res || null;
}

export async function fetchPuzzlePanelStats({ userId = '' } = {}) {
  if (!hasConfig()) return null;
  if (!userId) {
    return {
      puzzlePoints: 0,
      puzzleRating: 500,
      rank: null,
      solved: 0,
      attempts: 0,
      accuracy: 0
    };
  }

  // Preferred path: RPC with global rank support (security definer in DB).
  try {
    const rpc = await apiRpc('get_puzzle_user_panel_stats', {});
    const row = Array.isArray(rpc) ? (rpc[0] || null) : rpc;
    if (row) {
      const solved = Number(row.solved_count || 0);
      const attempts = Number(row.attempt_count || 0);
      const accuracy = Number.isFinite(Number(row.accuracy_pct))
        ? Number(row.accuracy_pct)
        : (attempts > 0 ? Math.round((solved / attempts) * 100) : 0);
      const resolvedRank = Number(
        row.global_rank ?? row.globalRank ?? row.rank ?? NaN
      );
      return {
        puzzlePoints: Number(row.puzzle_points_total || 0),
        puzzleRating: Number.isFinite(Number(row.puzzle_rating)) ? Number(row.puzzle_rating) : 500,
        rank: Number.isFinite(resolvedRank) ? resolvedRank : null,
        solved,
        attempts,
        accuracy
      };
    }
  } catch {
    // fallback below
  }

  // Fallback path (works without extra DB function; rank may be unavailable due RLS)
  const token = getAccessToken() || SUPABASE_KEY;
  let points = 0;
  try {
    const prof = await apiGet(
      `/rest/v1/profiles?id=eq.${encodeURIComponent(userId)}&select=puzzle_points_total,puzzle_rating&limit=1`,
      token
    );
    points = Number((prof && prof[0] && prof[0].puzzle_points_total) || 0);
    const rating = Number((prof && prof[0] && prof[0].puzzle_rating) || 500);
    const fallbackRows = await apiGet(
      `/rest/v1/puzzle_attempts?user_id=eq.${encodeURIComponent(userId)}&select=tries,success&limit=50000`,
      token
    );
    const solved = (fallbackRows || []).filter((r) => r?.success === true).length;
    const attempts = (fallbackRows || []).reduce((acc, r) => acc + Math.max(1, Number(r?.tries || 1)), 0);
    const accuracy = attempts > 0 ? Math.round((solved / attempts) * 100) : 0;
    let rank = null;
    try {
      const higherRows = await apiGet(
        `/rest/v1/profiles?select=id&puzzle_points_total=gt.${Math.max(0, points)}&limit=50000`,
        token
      );
      rank = Math.max(1, Number((higherRows || []).length) + 1);
    } catch {
      rank = 1;
    }
    return {
      puzzlePoints: points,
      puzzleRating: Number.isFinite(rating) ? rating : 500,
      rank,
      solved,
      attempts,
      accuracy
    };
  } catch {
    points = 0;
  }
  return {
    puzzlePoints: points,
    puzzleRating: 500,
    rank: null,
    solved: 0,
    attempts: 0,
    accuracy: 0
  };
}

function getDifficultyBucketIdFromRating(rating) {
  const r = Number(rating || 0);
  if (r < 1000) return 'easy';
  if (r < 1300) return 'medium';
  if (r < 1600) return 'advanced';
  if (r < 2000) return 'hard';
  return 'expert';
}

function normalizeThemes(value) {
  if (Array.isArray(value)) {
    return value.map((t) => String(t || '').trim().toLowerCase()).filter(Boolean);
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('{') && trimmed.endsWith('}')) {
      return trimmed
        .slice(1, -1)
        .split(',')
        .map((t) => String(t || '').trim().toLowerCase())
        .filter(Boolean);
    }
    return [trimmed.toLowerCase()];
  }
  return [];
}

export async function fetchPuzzleStartsByThemeGroup({
  userId = '',
  activeThemes = []
} = {}) {
  if (!hasConfig() || !userId) return {};
  const activeSet = new Set((activeThemes || []).map((t) => String(t || '').toLowerCase()));
  if (!activeSet.size) return {};

  try {
    const attemptRows = await apiGet(
      `/rest/v1/puzzle_attempts?select=puzzle_catalog_id,success,result&user_id=eq.${encodeURIComponent(
        userId
      )}&or=(success.eq.true,result.eq.success,result.eq.success_hint)&limit=50000`,
      getAccessToken() || SUPABASE_KEY
    );

    const solvedIds = Array.from(
      new Set(
        (attemptRows || [])
          .map((r) => Number(r?.puzzle_catalog_id))
          .filter((n) => Number.isFinite(n) && n > 0)
      )
    );
    if (!solvedIds.length) return {};

    const byKey = {};
    const chunkSize = 300;
    for (let i = 0; i < solvedIds.length; i += chunkSize) {
      const chunk = solvedIds.slice(i, i + chunkSize);
      const inList = chunk.join(',');
      if (!inList) continue;

      const puzzleRows = await apiGet(
        `/rest/v1/puzzles_catalog?select=id,rating,themes&id=in.(${inList})`,
        getAccessToken() || SUPABASE_KEY
      );

      for (let j = 0; j < (puzzleRows || []).length; j += 1) {
        const row = puzzleRows[j];
        const themes = normalizeThemes(row?.themes);
        const moduleTheme = themes.find((t) => activeSet.has(t));
        if (!moduleTheme) continue;
        const groupId = getDifficultyBucketIdFromRating(row?.rating);
        const key = `${moduleTheme}:${groupId}`;
        byKey[key] = Number(byKey[key] || 0) + 1;
      }
    }

    Object.keys(byKey).forEach((k) => {
      byKey[k] = Math.min(5, Math.max(0, Number(byKey[k] || 0)));
    });
    return byKey;
  } catch {
    return {};
  }
}

export function getPuzzleTierFromRating(puzzleRating = 500) {
  const r = Number(puzzleRating || 500);
  if (r >= 2000) return 'expert';
  if (r >= 1600) return 'hard';
  if (r >= 1300) return 'advanced';
  if (r >= 1000) return 'medium';
  return 'easy';
}

export function computeAdaptivePuzzleRange(puzzleRating = 500) {
  const r = Math.max(500, Math.min(3000, Number(puzzleRating || 500)));
  let min = r - 150;
  let max = r + 100;

  if (r >= 2000) {
    min = r - 220;
    max = r + 180;
  } else if (r >= 1600) {
    min = r - 200;
    max = r + 140;
  } else if (r >= 1300) {
    min = r - 180;
    max = r + 120;
  } else if (r >= 1000) {
    min = r - 180;
    max = r + 120;
  }

  return {
    minRating: Math.max(500, Math.floor(min)),
    maxRating: Math.min(3000, Math.ceil(max))
  };
}

export async function fetchUserPuzzleSkill({ userId = '' } = {}) {
  if (!hasConfig() || !userId) {
    return {
      puzzlePoints: 0,
      puzzleRating: 500
    };
  }

  const token = getAccessToken() || SUPABASE_KEY;
  let row = null;

  // Try with puzzle_rating column first (new schema), then fallback for old schema.
  try {
    const rows = await apiGet(
      `/rest/v1/profiles?id=eq.${encodeURIComponent(userId)}&select=puzzle_points_total,puzzle_rating&limit=1`,
      token
    );
    row = (rows || [])[0] || null;
  } catch {
    try {
      const rows = await apiGet(
        `/rest/v1/profiles?id=eq.${encodeURIComponent(userId)}&select=puzzle_points_total&limit=1`,
        token
      );
      row = (rows || [])[0] || null;
    } catch {
      row = null;
    }
  }

  const puzzlePoints = Number(row?.puzzle_points_total || 0);
  // If DB rating doesn't exist yet, keep neutral default.
  const puzzleRating = Number.isFinite(Number(row?.puzzle_rating))
    ? Number(row.puzzle_rating)
    : 500;

  return {
    puzzlePoints,
    puzzleRating
  };
}
